<template>
  <div class="row">
    <div class="col-sm-12">
      <p>{{ trans('subscription.subscribable_groupings.rules_explanation') }}</p>
    </div>

    <div class="col-sm-12">
      <input-component
        id="name"
        v-model="name"
        type="text"
        :value="name"
        :label="trans('core.name')"
        :errormessage="getErrorMessage('name')"
      />
    </div>

    <div class="col-sm-12">
      <textarea-component
        id="description"
        v-model="description"
        :value="description"
        :label="trans('core.description')"
        :errormessage="getErrorMessage('description')"
      />
    </div>

    <div class="col-sm-12">
      <selector-component
        id="category"
        v-model="category"
        :options="categorySelectOptions"
        :value="category"
        :label="trans('core.category')"
        :errormessage="getErrorMessage('category')"
      />
    </div>

    <div
      v-if="category !== 'advanced_rules'"
      class="col-sm-12"
    >
      <input-component
        id="number"
        v-model="number"
        type="number"
        :value="number"
        :label="trans('subscription.subscribable_groupings.student_should_subscribe')"
        :errormessage="getErrorMessage('number')"
      />
    </div>

    <div
      v-if="characteristicRequiredCategories.includes(category)"
      class="col-sm-12"
    >
      <multi-select-component
        id="student_characteristic_multi_select"
        v-model="studentCharacteristic"
        type="student-characteristic"
        :label="trans('subscription.subscribable_groupings.student_characteristic')"
        :include="{ 'type': 'boolean' }"
        :search-at-mounted="true"
        :placeholder="trans('core.type_or_click') + ' ...'"
        :max-selected-options="1"
        :errormessage="getErrorMessage('student_characteristic')"
      />
    </div>

    <div class="col-sm-12">
      <p class="text-margin">
        {{ trans('subscription.subscribable_groupings.dates_explanation') }}
      </p>
    </div>

    <div class="col-sm-12">
      <input
        id="sidebarCheckbox"
        v-model="showSidebarDates"
        type="checkbox"
      >
      <label for="sidebarCheckbox">
        {{ trans('subscription.subscribable_groupings.dates_label') }}
      </label>
    </div>

    <template v-if="showSidebarDates">
      <div
        class="col-sm-12"
      >
        <date-picker-component
          id="enddate"
          v-model="visibleFrom"
          :label="trans('subscription.subscribable_groupings.visible_from')"
          :errormessage="getErrorMessage('visible_from')"
        />
      </div>

      <div
        class="col-sm-12"
      >
        <date-picker-component
          id="enddate"
          v-model="visibleTill"
          :label="trans('subscription.subscribable_groupings.visible_till')"
          :errormessage="getErrorMessage('visible_till')"
        />
      </div>
    </template>

    <div
      v-if="category == 'advanced_rules'"
      class="col-sm-12"
    >
      <RuleBuilder v-model="validationRules" />
    </div>

    <div class="col-sm-12">
      <p class="text-margin">
        {{ trans('subscription.subscribable_groupings.send_notification_explanation') }}
      </p>
    </div>
    <div class="col-sm-12">
      <input
        id="sendNotification"
        v-model="sendNotification"
        type="checkbox"
        :errormessage="getErrorMessage('send_notification')"
      >
      <label for="sendNotification">
        {{ trans('subscription.send_notification_to_students') }}
      </label>
    </div>
  </div>
</template>

<script>
import InputComponent from '@/employees/components/FormComponents/InputComponent'
import TextareaComponent from '@/employees/components/FormComponents/TextareaComponent'
import SelectorComponent from '@/employees/components/FormComponents/SelectorComponent'
import MultiSelectComponent from '@/employees/components/FormComponents/MultiSelectComponent'
import RuleBuilder from '@/employees/components/Rule/utilities/RuleBuilder'
import DatePickerComponent from '../FormComponents/DatePickerComponent.vue'

export default {
  name: 'GroupingRules',
  components: { MultiSelectComponent, SelectorComponent, InputComponent, TextareaComponent, RuleBuilder, DatePickerComponent },
  props: {
    value: {
      required: true,
      type: Object,
    },
    categories: {
      required: true,
      type: Array,
    },
    characteristicRequiredCategories: {
      required: true,
      type: Array,
    },
    errors: {
      required: false,
      default: () => {},
      type: Object,
    },
    programId: {
      required: true,
      type: String,
    },
  },

  data () {
    return {
      pickerOptions: {
        firstDayOfWeek: 1,
      },
    }
  },

  computed: {
    name: {
      get () {
        return this.value.name
      },
      set (value) {
        this.emitInput('name', value)
      },
    },

    description: {
      get () {
        return this.value.description
      },
      set (value) {
        this.emitInput('description', value)
      },
    },

    number: {
      get () {
        return this.value.number
      },
      set (value) {
        this.emitInput('number', value)
      },
    },

    category: {
      get () {
        return this.value.category
      },
      set (value) {
        this.emitInput('category', value)
      },
    },

    validationRules: {
      get () {
        return this.value.validationRules
      },
      set (value) {
        this.emitInput('validationRules', value)
      },
    },

    studentCharacteristic: {
      get () {
        return this.value.studentCharacteristic
      },
      set (value) {
        this.emitInput('studentCharacteristic', value)
      },
    },

    visibleFrom: {
      get () {
        return this.value.visibleFrom
      },
      set (value) {
        this.emitInput('visibleFrom', value)
      },
    },

    visibleTill: {
      get () {
        return this.value.visibleTill
      },
      set (value) {
        this.emitInput('visibleTill', value)
      },
    },

    sendNotification: {
      get () {
        return this.value.sendNotification
      },
      set (value) {
        this.emitInput('sendNotification', value)
      },
    },

    showSidebarDates: {
      get () {
        return this.value.showSidebarDates
      },
      set (value) {
        this.emitInput('showSidebarDates', value)
      },
    },

    categorySelectOptions () {
      const options = {}
      this.categories.forEach(function (option) {
        options[option] = Lang.trans('subscription.subscribable_groupings.categories.' + option)
      })

      return options
    },

  },

  created () {
    this.$emit('canFinishStep')
  },

  methods: {
    emitInput (key, value) {
      const newValue = { ...this.value, [key]: value }

      this.$emit('input', newValue)
    },

    getErrorMessage (field) {
      if (this.errors && Object.keys(this.errors).includes(field)) {
        return this.errors[field].join(' ')
      }
    },
  },
}
</script>

<style>
    .text-margin {
        margin-top: 10px;
    }
</style>
